import React, { useMemo } from 'react'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { CssBaseline } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '../hooks/redux'
import { lightTheme, darkTheme } from '../theme'

/**
 * ThemeProviderWrapper
 * 
 * Wraps the app with MUI ThemeProvider and connects it to Redux theme state.
 * Automatically switches between light and dark themes based on user preference.
 * Also respects RTL direction based on the current language.
 */
export const ThemeProviderWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const themeMode = useAppSelector((state) => state.ui.theme)
  const { i18n } = useTranslation()
  const isRtl = i18n.language === 'ar'

  const theme = useMemo(() => {
    const baseTheme = themeMode === 'dark' ? darkTheme : lightTheme

    // Merge RTL direction and alignment into the theme
    return createTheme({
      ...baseTheme,
      direction: isRtl ? 'rtl' : 'ltr',
      typography: {
        ...baseTheme.typography,
        fontFamily: isRtl
          ? "'Cairo', 'Segoe UI', 'Roboto', 'Arial', sans-serif"
          : baseTheme.typography?.fontFamily || "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
      },
      components: {
        ...baseTheme.components,
        MuiTableCell: {
          styleOverrides: {
            root: {
              '&:not(.MuiTableCell-alignCenter):not(.MuiTableCell-alignRight):not(.MuiTableCell-alignLeft)': {
                textAlign: isRtl ? 'right' : 'left',
              },
            },
            head: {
              '&:not(.MuiTableCell-alignCenter):not(.MuiTableCell-alignRight):not(.MuiTableCell-alignLeft)': {
                textAlign: isRtl ? 'right' : 'left',
              },
            },
          },
        },
        MuiListItemText: {
          styleOverrides: {
            root: {
              textAlign: isRtl ? 'right' : 'left',
            },
          },
        },
        MuiDialogTitle: {
          styleOverrides: {
            root: {
              textAlign: isRtl ? 'right' : 'left',
            },
          },
        },
      },
    })
  }, [themeMode, isRtl])

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  )
}
